import React, { useEffect, useState, useRef } from "react";
import { useI18n } from "../context/I18nContext";
import { api } from "../lib/api";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Check, ArrowRight, Loader2, XCircle } from "lucide-react";

export default function CheckoutSuccess() {
  const { t, lang } = useI18n();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error
  const [tx, setTx] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) return setStatus("error");

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payment/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attemptsRef.current >= 6) {
          setStatus("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attemptsRef.current >= 6) setStatus("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
  }, [params]);

  return (
    <div className="min-h-[70vh] grid place-items-center px-6 py-16">
      <div className="max-w-xl w-full bg-white border border-slate-200 rounded-sm p-10 text-center" data-testid="checkout-success-card">
        {status === "polling" && (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-slate-400 mx-auto" />
            <h1 className="mt-6 font-display text-2xl font-bold text-slate-900">
              {lang === "fr" ? "Vérification du paiement…" : "Verifying payment…"}
            </h1>
            <p className="mt-2 text-sm text-slate-500">{lang === "fr" ? "Quelques secondes." : "A few seconds."}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="h-12 w-12 bg-emerald-700 text-white mx-auto grid place-items-center rounded-sm">
              <Check className="h-6 w-6" />
            </div>
            <h1 className="mt-6 font-display text-3xl font-bold text-slate-900" data-testid="status-paid">
              {lang === "fr" ? "Paiement confirmé" : "Payment confirmed"}
            </h1>
            <p className="mt-3 text-slate-600">
              {lang === "fr"
                ? `Votre souscription de ${tx?.amount?.toLocaleString()}€ est en examen conformité.`
                : `Your ${tx?.amount?.toLocaleString()}€ subscription is in compliance review.`}
            </p>
            <Button className="mt-8 rounded-sm bg-slate-900 hover:bg-slate-800" onClick={() => nav("/dashboard")} data-testid="go-dashboard">
              {t.nav.dashboard} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </>
        )}
        {(status === "expired" || status === "error") && (
          <>
            <XCircle className="h-10 w-10 text-slate-500 mx-auto" />
            <h1 className="mt-6 font-display text-2xl font-bold text-slate-900">
              {lang === "fr" ? "Paiement non confirmé" : "Payment not confirmed"}
            </h1>
            <p className="mt-2 text-sm text-slate-500">{lang === "fr" ? "Veuillez réessayer depuis la page Investir." : "Please retry from the Invest page."}</p>
            <Button asChild className="mt-8 rounded-sm" variant="outline">
              <Link to="/invest">{t.nav.invest}</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
